import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { useTooltip } from '@visx/tooltip';
import { TooltipData } from '../../components/ChartTooltip/ChartTooltip';
import {
  FormatUtils,
  getMapColors,
  MAP_CHART_FIELDS_LABEL,
  METRICS,
  useWindowSize,
  getPopulationPerc,
  isReach
} from '../../utils';
import uniqid from 'uniqid';
import styled from 'styled-components';
import * as topojson from 'topojson-client';
import topology from './world-topology.json';
import { Mercator } from '@visx/geo';
import { scaleLinear } from '@visx/scale';
import { localPoint } from '@visx/event';
import { BoxV2, EmptyStateV2 } from '@cubeyou-team/hx-react-components';
import ChartTooltip from '../../components/ChartTooltip';
import countriesAbbr from './world-abbreviations.json';
import { Group } from '@visx/group';
import { LinearGradient } from '@visx/gradient';
import { Text } from '@visx/text';

type WorldMapChartProps = {
  data: any[];
  metric: string;
  color: string;
  emptyDataMessage: string;
};

const StyledWorldMapChartWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  background-color: #ffffff;
  padding: 10px;
`;

interface FeatureShape {
  type: 'Feature';
  id: string;
  geometry: { coordinates: [number, number][][]; type: 'Polygon' };
  properties: { name: string };
}

const world = topojson.feature(topology, topology.objects.units) as {
  type: 'FeatureCollection';
  features: FeatureShape[];
};

let tooltipTimeout: number;

const WorldMapChart: FC<WorldMapChartProps> = ({
  data,
  metric,
  color,
  emptyDataMessage
}) => {
  const svgRef = useRef(null);
  const chartId = useRef(uniqid());
  const isEmpty = !data || data.length === 0;
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);

  const centerX = width / 2;
  const centerY = height / 2;
  const scale = (width / 630) * 100;

  const max = (data && Math.max(...data.map((item) => item[metric]))) || 0;
  const min = (data && Math.min(...data.map((item) => item[metric]))) || 0;
  const {
    tooltipOpen,
    tooltipLeft,
    tooltipTop,
    tooltipData,
    hideTooltip,
    showTooltip
  } = useTooltip<TooltipData>();

  const windowSize = useWindowSize();

  const chartRef = useCallback(
    (node) => {
      if (node != null) {
        setWidth(node.getBoundingClientRect().width);
        setHeight(node.getBoundingClientRect().height);
      }
    },
    [windowSize]
  );
  const colorsRange = getMapColors(color);
  const colorScale = useMemo(
    () =>
      scaleLinear({
        domain: [
          metric === METRICS.AFFINITY ? min : min * 100,
          metric === METRICS.AFFINITY ? max : max * 100
        ],
        range: colorsRange
      }),
    [metric, min, max, color]
  );

  const getFillColor = useCallback(
    (item) => {
      if (!metric) {
        return '#d8d7ea';
      }

      let value = item[metric] || 0;
      if (metric !== METRICS.AFFINITY) {
        value = value * 100;
      }
      return colorScale(value);
    },
    [metric, colorScale]
  );

  const handleMouseMove = useCallback(
    (event: React.MouseEvent, feature: FeatureShape, color = '#000000') => {
      if (tooltipTimeout) clearTimeout(tooltipTimeout);
      if (!svgRef.current) return;
      const point = localPoint(svgRef.current, event);
      showTooltip({
        tooltipData: {
          ...feature,
          color: color
        },
        tooltipTop: point?.y,
        tooltipLeft: point?.x
      });
    },
    [svgRef, showTooltip]
  );

  const handleMouseLeave = useCallback(
    (event) => {
      tooltipTimeout = window.setTimeout(() => {
        hideTooltip();
      }, 0);
    },
    [hideTooltip]
  );

  if (isEmpty) {
    return (
      <StyledWorldMapChartWrapper>
        <BoxV2 minWidth='100%'>
          <EmptyStateV2
            icon='analytics'
            mainText={emptyDataMessage}
            size='large'
          />
        </BoxV2>
      </StyledWorldMapChartWrapper>
    );
  }

  const checkGreatBritain = (featureId, itemId) =>
    featureId === 'UK' && itemId === 'GB';

  return (
    <StyledWorldMapChartWrapper ref={chartRef}>
      <svg width={width} height={height} ref={svgRef}>
        <rect
          x={0}
          y={0}
          width={width}
          height={height}
          fill='#ffffff'
          rx={14}
        />
        <Mercator
          data={world.features}
          scale={scale}
          translate={[centerX, centerY + 50]}
        >
          {(mercator) => (
            <Group key={`group-${chartId.current}`}>
              {mercator.features.map(({ feature, path }, i) => {
                const abbr = countriesAbbr.find(
                  (abbr) => abbr.country === feature.properties.name
                );
                const included =
                  data &&
                  data.find(
                    (item: any) =>
                      abbr &&
                      (item.id === abbr.abbreviation ||
                        checkGreatBritain(abbr.abbreviation, item.id))
                  );

                if (included) {
                  return (
                    <path
                      key={`map-feature-${i}`}
                      d={path || ''}
                      fill={getFillColor(included)}
                      stroke='#ffffff'
                      strokeWidth={0.5}
                      onMouseOver={(event) =>
                        handleMouseMove(event, included, getFillColor(included))
                      }
                      onMouseMove={(event) =>
                        handleMouseMove(event, included, getFillColor(included))
                      }
                      onMouseLeave={(event) => handleMouseLeave(event)}
                    />
                  );
                }

                if (abbr && abbr.country === 'Antarctica') {
                  return null;
                }
                return (
                  <path
                    key={`map-feature-${i}`}
                    d={path || ''}
                    fill='#d8d7ea'
                    stroke='#ffffff'
                    strokeWidth={0.5}
                  />
                );
              })}
            </Group>
          )}
        </Mercator>
        {metric && (
          <Group top={height - 40} left={centerX - 100}>
            <LinearGradient
              from={colorsRange[0]}
              to={colorsRange[1]}
              id={`legend-gradient-${color}`}
              vertical={false}
            />
            <Text
              fontSize='10px'
              fontWeight='bold'
              textAnchor='middle'
              verticalAnchor='middle'
              fill='#000000'
              width={100}
              height={20}
              x={0}
              y={-10}
            >
              {FormatUtils.formattedValue(min, metric)}
            </Text>
            <Text
              fontSize='10px'
              fontWeight='bold'
              textAnchor='middle'
              verticalAnchor='middle'
              fill='#000000'
              width={100}
              height={20}
              x={200}
              y={-10}
            >
              {`${FormatUtils.formattedValue(max, metric)}`}
            </Text>
            <rect
              width={200}
              height={20}
              fill={`url(#legend-gradient-${color})`}
            />
            <Text
              fontSize='10px'
              fontWeight='bold'
              textAnchor='middle'
              verticalAnchor='middle'
              fill='#000000'
              width={100}
              height={20}
              x={100}
              y={30}
            >
              {MAP_CHART_FIELDS_LABEL[metric]}
            </Text>
          </Group>
        )}
      </svg>
      {tooltipOpen && tooltipData && (
        <ChartTooltip
          opened={tooltipOpen}
          top={tooltipTop || 0}
          left={tooltipLeft || 0}
          data={tooltipData}
        >
          {isReach(metric) && (
            <div>
              <span>Reach: </span>
              {FormatUtils.formattedValue(
                getPopulationPerc(tooltipData),
                METRICS.REACH
              )}
            </div>
          )}
          {metric === METRICS.AFFINITY && (
            <div>
              <span>Affinity: </span>
              {FormatUtils.formattedValue(
                tooltipData.target_audience_affinity,
                METRICS.AFFINITY
              )}
            </div>
          )}
          {metric === METRICS.BASE && (
            <div>
              <span>Base: </span>
              {FormatUtils.formattedValue(
                tooltipData.base_population_perc,
                METRICS.BASE
              )}
            </div>
          )}
          {metric === METRICS.PENETRATION && (
            <div>
              <span>Penetration: </span>
              {FormatUtils.formattedValue(
                tooltipData.penetration,
                METRICS.PENETRATION
              )}
            </div>
          )}
          {metric === METRICS.RELEVANCY && (
            <div>
              <span>Relevance: </span>
              {FormatUtils.formattedValue(
                tooltipData.relevancy,
                METRICS.RELEVANCY
              )}
            </div>
          )}
        </ChartTooltip>
      )}
    </StyledWorldMapChartWrapper>
  );
};

export default WorldMapChart;
